import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ClearTwoToneIcon from "@mui/icons-material/ClearTwoTone";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import HourglassTopTwoToneIcon from "@mui/icons-material/HourglassTopTwoTone";
import HelpOutlineTwoToneIcon from "@mui/icons-material/HelpOutlineTwoTone";

import axios from "../../api/axios";
import CustomTable from "../../components/customTable";
import Layout from "../../layout/layout";

const headCells = [
  {
    id: "id",
    numeric: false,
    disablePadding: true,
    label: "ID",
  },
  {
    id: "user_name",
    numeric: false,
    disablePadding: false,
    label: "User",
  },
  {
    id: "payment_method",
    numeric: false,
    disablePadding: false,
    label: "Method",
  },
  {
    id: "amount",
    numeric: false,
    disablePadding: false,
    label: "Amount",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Transaction Status",
    render: (row) => renderTransactionStatusIcon(row.status),
  },
  {
    id: "invoice_status",
    numeric: false,
    disablePadding: false,
    label: "Invoice Status",
  },
  {
    id: "paid_status",
    numeric: false,
    disablePadding: false,
    label: "Paid Status",
  },
  {
    id: "transaction_id",
    numeric: false,
    disablePadding: false,
    label: "Invoice ID",
  },
  {
    id: "customer_reference",
    numeric: false,
    disablePadding: false,
    label: "Customer Reference",
  },
  {
    id: "date",
    numeric: false,
    disablePadding: false,
    label: "Date",
  },
];

function renderTransactionStatusIcon(status) {
  const normalizedStatus = String(status ?? "").toLowerCase();

  if (normalizedStatus === "success") {
    return (
      <Tooltip title="Successful">
        <CheckCircleTwoToneIcon color="success" />
      </Tooltip>
    );
  }

  if (normalizedStatus === "failed") {
    return (
      <Tooltip title="Failed">
        <CancelTwoToneIcon color="error" />
      </Tooltip>
    );
  }

  if (normalizedStatus === "pending") {
    return (
      <Tooltip title="Pending">
        <HourglassTopTwoToneIcon color="warning" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={status || "Unknown"}>
      <HelpOutlineTwoToneIcon color="disabled" />
    </Tooltip>
  );
}

const defaultSummary = {
  successful: 0,
  failed: 0,
  paid: 0,
};

function SummaryCard({ title, value, color }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h4" sx={{ color, fontWeight: 700 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function formatCurrency(value) {
  const numericValue = Number(value ?? 0);
  return `KWD ${numericValue.toFixed(3)}`;
}

function parseDateParts(value) {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value);
  const [yearPart, monthPart] = normalizedValue.split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);

  if (!Number.isNaN(year) && !Number.isNaN(month)) {
    return { year, month };
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return {
    year: parsedDate.getFullYear(),
    month: parsedDate.getMonth() + 1,
  };
}

export default function MyFatoorahPaymentStatusPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchMyFatoorahPaymentStatuses = async () => {
      setLoading(true);
      try {
        const response = await axios.get("transaction/myfatoorah-status/");
        if (response.status === 200) {
          setRows(response.data?.data ?? []);
          setSummary(response.data?.summary ?? defaultSummary);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchMyFatoorahPaymentStatuses();
  }, []);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return rows;
    }

    return rows.filter((row) =>
      [
        row.user_name,
        row.payment_method,
        row.status,
        row.invoice_status,
        row.paid_status,
        row.transaction_id,
        row.customer_reference,
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [rows, search]);

  const incomeSummary = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    return rows.reduce(
      (accumulator, row) => {
        const isPaid =
          Boolean(row.is_paid) ||
          String(row.paid_status ?? "").toLowerCase() === "paid";

        if (!isPaid) {
          return accumulator;
        }

        const amount = Number(row.amount ?? 0);
        if (Number.isNaN(amount)) {
          return accumulator;
        }

        accumulator.income_all_time += amount;

        const dateParts = parseDateParts(row.date);
        if (!dateParts) {
          return accumulator;
        }

        if (dateParts.year === currentYear) {
          accumulator.income_this_year += amount;
          if (dateParts.month === currentMonth) {
            accumulator.income_this_month += amount;
          }
        }

        return accumulator;
      },
      {
        income_this_month: 0,
        income_this_year: 0,
        income_all_time: 0,
      }
    );
  }, [rows]);

  const body = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Income This Month"
            value={formatCurrency(incomeSummary.income_this_month)}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Income This Year"
            value={formatCurrency(incomeSummary.income_this_year)}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Income All Time"
            value={formatCurrency(incomeSummary.income_all_time)}
            color="primary.main"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total Successful"
            value={summary.successful}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard title="Total Failed" value={summary.failed} color="error.main" />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard title="Total Paid" value={summary.paid} color="primary.main" />
        </Grid>
      </Grid>

      <TextField
        label="Search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ width: { xs: "100%", md: "45%" } }}
        InputProps={{
          endAdornment: search ? (
            <IconButton onClick={() => setSearch("")}>
              <ClearTwoToneIcon />
            </IconButton>
          ) : null,
        }}
      />

      <CustomTable headCells={headCells} rows={filteredRows} loading={loading} />
    </Box>
  );

  return <Layout body={body} title="MyFatoorah Payment Status" />;
}
